import { useState } from "react";

interface StepDetail {
  label1: string;
  val1: string;
  label2: string;
  val2: string;
  body: string;
}

interface StepItem {
  num: string;
  name: string;
  tag: string;
  desc: string;
  detail: StepDetail;
}

interface UserTab {
  key: string;
  icon: string;
  name: string;
  role: string;
  conflict: string;
  value: string;
  emotion: string;
  cta: string;
  steps: StepItem[];
}

const tabs: UserTab[] = [
  {
    key: "discoverer",
    icon: "ri-microscope-line",
    name: "科学发现者",
    role: "想创新，怕选错题",
    conflict: "想创新，但怕选错题、做无效创新",
    value: "把模糊想法变成可发表研究",
    emotion: "我不是不会努力，我是怕努力错了方向",
    cta: "立即开始探索",
    steps: [
      {
        num: "01", name: "科学对话（极速模式）", tag: "入口",
        desc: "快速摸底研究方向，定位当前卡点",
        detail: { label1: "核心作用", val1: "磐石的核心对话入口，快速问答与即时答疑", label2: "模式特点", val2: "简洁高效，极速响应，适合日常问题解答与方向初步判断", body: "作为整个路径的起点，通过极速模式快速定位研究方向的核心卡点，明确下一步检索的方向和目标。" }
      },
      {
        num: "02", name: "文献罗盘", tag: "检索",
        desc: "极速检索近 3 年文献，摸清研究现状与空白",
        detail: { label1: "核心作用", val1: "学术文献一站式检索与阅读", label2: "数据规模", val2: "聚合中科院 1.7 亿科技论文、arXiv 240 万预印本", body: "极速检索近 3 年文献，快速建立领域认知，找到研究空白点。输出文献列表支持全文 PDF 阅读。" }
      },
      {
        num: "03", name: "科学对话（研究模式）", tag: "分析",
        desc: "基于文献数据，生成初步研究思路",
        detail: { label1: "核心作用", val1: "全流程深度调研，输出结构化调研报告", label2: "模式特点", val2: "动态检索文献库+网络资源+工具，持续多轮思考→执行→验证", body: "基于文献罗盘检索结果，生成初步研究思路和假设，输出带结构化结论的决策参考。" }
      },
      {
        num: "04", name: "创新评价", tag: "评估",
        desc: "评估选题的创新性与可行性，规避无效创新",
        detail: { label1: "核心作用", val1: "对选题、申请书、论文进行多维评估（创新性、可行性、风险）", label2: "核心价值", val2: "把模糊判断变成有依据、有底气的决策", body: "评估研究方向的创新性与可行性，这是路径中的决策关键节点，帮用户规避无效创新。" }
      },
      {
        num: "05", name: "ScienceClaw（磐石科学家）", tag: "研讨",
        desc: "与 AI 专家深度讨论技术路线，确认可行性",
        detail: { label1: "核心作用", val1: "垂直领域 AI 专家，支持单聊咨询与研讨厅群聊协作", label2: "能力特点", val2: "记忆用户偏好、沉淀工作流、积累知识网络", body: "与磐石科学家就技术路线可行性展开深度讨论，补充文献和数据之外的经验判断层。" }
      },
      {
        num: "06", name: "深度研究", tag: "调研",
        desc: "多智能体协作，输出完整调研报告与实验方案",
        detail: { label1: "核心作用", val1: "多智能体协作深度调研，动态检索文献+网络+工具", label2: "界面特点", val2: "左侧思考链+执行日志，右侧任务结果/信息/文件三栏", body: "多智能体协作输出完整调研报告与实验方案，适用于技术可行性分析、市场调研等复杂调研任务。" }
      },
      {
        num: "07", name: "知识库", tag: "沉淀",
        desc: "沉淀文献、笔记与研究结论，构建个人知识网络",
        detail: { label1: "核心作用", val1: "个人/团队文献与知识的存储、管理、检索平台，支持 RAG+知识图谱", label2: "核心能力", val2: "文献收藏、分库管理、全库对话、知识认知网络构建", body: "将调研过程和结论沉淀归档，构建个人专属领域知识库，后续评估新方向时可直接调出历史对比。" }
      },
      {
        num: "08", name: "科学对话（学术综述 / PPT / 海报）", tag: "产出",
        desc: "产出综述、汇报材料或会议海报，完成成果输出",
        detail: { label1: "学术综述", val1: "超长文综述，可确认大纲再生成，适合万字级别文献综述", label2: "学术 PPT", val2: "上传文献一键生成，配图可溯源，适合开题报告、学术汇报", body: "最终成果输出阶段，将所有研究积累转化为可发表、可演示的成果形式，完成从想法到完整输出的闭环。" }
      },
    ],
  },
  {
    key: "consumer",
    icon: "ri-book-open-line",
    name: "知识消费者",
    role: "想看懂，怕被误导",
    conflict: "想看懂前沿，但容易被复杂信息和 AI 幻觉误导",
    value: "把复杂知识变成能理解、能应用的内容",
    emotion: "我不是不想学，我是缺一个真正带我学明白的系统",
    cta: "立即开始学习",
    steps: [
      {
        num: "01", name: "科研资讯", tag: "入口",
        desc: "订阅领域最新论文与学术动态，发现值得深入的内容",
        detail: { label1: "核心作用", val1: "学术资讯聚合平台，汇聚 100+ 学术信源（arXiv、期刊新闻、科技媒体）", label2: "核心能力", val2: "论文/新闻卡片流、AI 资讯解读（结构化摘要、趋势分析）", body: "追踪领域最新进展，发现值得深入研究的论文，是平台的流量漏斗起点。" }
      },
      {
        num: "02", name: "科学对话（极速模式）", tag: "解读",
        desc: "对新收到的文献做初步解读，破除语言和术语障碍",
        detail: { label1: "核心作用", val1: "对收到的新文献做初步解读，破除语言和术语障碍", label2: "模式特点", val2: "简洁高效，快速问答，适合即时答疑", body: "对科研资讯中发现的新文献做初步解读，降低阅读门槛，为后续深度学习铺路。" }
      },
      {
        num: "03", name: "ScienceClaw（磐石科学家）", tag: "研讨",
        desc: "与领域 AI 专家深入对话，把复杂概念翻译成易懂语言",
        detail: { label1: "核心作用", val1: "垂直领域 AI 专家，与用户直接对话，逐步积累偏好和知识网络", label2: "能力特点", val2: "记忆用户偏好、沉淀工作流、可组成研讨厅群聊脑暴", body: "与磐石科学家深入对话，把复杂概念翻译成自己能理解的语言，适合复杂交叉问题。" }
      },
      {
        num: "04", name: "知识库", tag: "沉淀",
        desc: "收藏有价值的内容，归类整理，构建个人学习知识库",
        detail: { label1: "核心作用", val1: "个人/团队文献与知识的存储、管理、检索平台", label2: "核心能力", val2: "文献收藏、批注笔记、论文导读（核心观点提取、引文回溯）", body: "收藏有价值的内容，归类整理，构建个人学习知识库，后续可直接调出历史积累做延伸学习。" }
      },
      {
        num: "05", name: "科学对话（学术综述）", tag: "整合",
        desc: "对已消化内容生成结构化综述，形成自己的认知框架",
        detail: { label1: "核心作用", val1: "超长文综述，可确认大纲再生成，适合万字级文献综述", label2: "模式特点", val2: "上传文献一键生成，配图可溯源", body: "对已消化内容生成结构化综述，形成自己的认知框架，完成从泛读到精读的升级。" }
      },
    ],
  },
  {
    key: "engineer",
    icon: "ri-settings-3-line",
    name: "工程实现者",
    role: "想出结果，怕白做",
    conflict: "想尽快出结果，但被试错、调参、实验流程拖住",
    value: "把想法稳定推进到实验结果，减少无效试错",
    emotion: "我不是怕难，我是怕一遍遍白做",
    cta: "立即开始实验",
    steps: [
      {
        num: "01", name: "科学对话（极速/研究模式）", tag: "入口",
        desc: "明确实验目标与核心指标，确定基准路线",
        detail: { label1: "核心作用", val1: "磐石核心对话入口，明确实验目标与核心指标，确定基准路线", label2: "模式特点", val2: "极速模式快速问答，研究模式全流程深度调研", body: "作为路径起点，在科学对话中明确实验目标与核心性能指标，快速对齐研究方向，避免后续无效探索。" }
      },
      {
        num: "02", name: "文献罗盘", tag: "调研",
        desc: "调研同类实验的参数配置与最优设置，减少试错起点",
        detail: { label1: "核心作用", val1: "学术文献一站式检索，对比不同技术路线的指标差异", label2: "核心价值", val2: "调研同类实验参数配置，减少试错起点", body: "检索同类实验的参数配置与最优设置，找到可靠的基准起点，避免从零开始摸索。" }
      },
      {
        num: "03", name: "深度研究", tag: "分析",
        desc: "调研技术路线，输出最优实验参数组合建议",
        detail: { label1: "核心作用", val1: "多智能体协作深度调研，动态检索文献+网络+工具", label2: "输出", val2: "完整调研报告、最优实验参数组合建议", body: "基于文献调研结果，输出技术路线分析报告与最优实验参数组合建议，为后续方案确认提供数据支撑。" }
      },
      {
        num: "04", name: "创新评价", tag: "评估",
        desc: "评估技术路线的可行性与风险，确认最优方案再动手",
        detail: { label1: "核心作用", val1: "对技术路线做多维评估（创新性、可行性、风险）", label2: "核心价值", val2: "替代靠经验拍脑袋，确认最优方案再动手", body: "在动手实验前对技术路线做最终可行性评估，识别风险点，确保投入产出比最优，避免资源浪费。" }
      },
      {
        num: "05", name: "知识库", tag: "沉淀",
        desc: "记录实验参数配置与结果，形成可复用的实验手册",
        detail: { label1: "核心作用", val1: "文献与知识的存储、管理、检索平台，支持 RAG+知识图谱", label2: "核心能力", val2: "批注笔记、实验参数记录、可复用实验手册", body: "记录实验参数配置与结果，形成可复用的实验手册，构建团队知识沉淀，后续类似实验可直接复用。" }
      },
    ],
  },
  {
    key: "manager",
    icon: "ri-bar-chart-box-line",
    name: "科研管理者",
    role: "要做决策，怕缺依据",
    conflict: "要做高风险科研决策，但缺乏结构化判断工具",
    value: "把碎片信息转化成可执行、可解释的方向判断",
    emotion: "我不是信息不够，我是缺一个敢让我做判断的依据系统",
    cta: "立即开始决策",
    steps: [
      {
        num: "01", name: "科学对话（研究模式）", tag: "入口",
        desc: "初步摸底研究方向的全貌，形成判断框架",
        detail: { label1: "核心作用", val1: "全流程深度调研，初步摸底研究方向全貌", label2: "模式特点", val2: "动态检索文献+网络+工具，输出结构化判断框架", body: "作为决策起点，通过研究模式初步摸底研究方向全貌，形成系统性的判断框架，避免仅凭直觉做决策。" }
      },
      {
        num: "02", name: "创新评价", tag: "评估",
        desc: "对多个候选方向做多维评估，建立优先级",
        detail: { label1: "核心作用", val1: "对多个候选方向做多维评估（创新性/可行性/风险），建立优先级", label2: "核心价值", val2: "把碎片化判断变成有底气的结构化决策依据", body: "对候选研究方向做系统化评估，建立优先级排序，为管理决策提供数据支撑，替代拍脑袋决策。" }
      },
      {
        num: "03", name: "文献罗盘 + 深度研究", tag: "调研",
        desc: "对重点方向做深度调研，获取结构化数据和证据链",
        detail: { label1: "文献罗盘", val1: "极速检索文献，建立领域认知", label2: "深度研究", val2: "多智能体协作，输出完整调研报告与结构化证据链", body: "对优先级最高的方向做深度调研，获取完整的结构化数据和证据链，为决策提供充分的事实依据。" }
      },
      {
        num: "04", name: "ScienceClaw（磐石研讨厅）", tag: "研讨",
        desc: "引入多个垂直领域 AI 专家，对技术路线做多方会诊",
        detail: { label1: "核心作用", val1: "多个专家智能体 + 用户共同脑暴，适合复杂交叉问题", label2: "能力特点", val2: "跨学科问题多方会诊，补充单一视角的盲区", body: "引入多个垂直领域 AI 专家组成研讨厅，对技术路线做多方会诊，获得跨学科视角的决策建议。" }
      },
    ],
  },
];

interface StepRowProps {
  step: StepItem;
  isLast: boolean;
}

function StepRow({ step, isLast }: StepRowProps) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div className="flex gap-4 mb-2">
        <div className="flex flex-col items-center flex-shrink-0 pt-1">
          <div className="w-7 h-7 rounded-full bg-[#3d69e8]/15 border border-[#3d69e8]/30 flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-[#3d69e8]">{step.num}</span>
          </div>
          {!isLast && <div className="w-px flex-1 bg-white/8 mt-2 min-h-[16px]"></div>}
        </div>
        <div
          className="flex-1 bg-[#161616] hover:bg-[#1c1c1c] border border-white/8 hover:border-white/15 rounded-xl p-4 cursor-pointer transition-all mb-2"
          onClick={() => setOpen(!open)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-medium text-white">{step.name}</span>
                <span className="text-[10px] bg-[#3d69e8]/15 text-[#6b90f0] px-2 py-0.5 rounded-full whitespace-nowrap">{step.tag}</span>
              </div>
              <p className="text-xs text-white/40">{step.desc}</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`text-white/30 text-sm ${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
            </div>
          </div>

          {open && (
            <div className="mt-4 pt-4 border-t border-white/8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="bg-[#0f0f0f] rounded-lg p-3">
                  <div className="text-[10px] text-white/30 mb-1">{step.detail.label1}</div>
                  <div className="text-xs text-white/70 leading-relaxed">{step.detail.val1}</div>
                </div>
                <div className="bg-[#0f0f0f] rounded-lg p-3">
                  <div className="text-[10px] text-white/30 mb-1">{step.detail.label2}</div>
                  <div className="text-xs text-white/70 leading-relaxed">{step.detail.val2}</div>
                </div>
              </div>
              <p className="text-xs text-white/40 leading-relaxed">{step.detail.body}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UserTabsSection() {
  const [activeTab, setActiveTab] = useState(0);
  const tab = tabs[activeTab];

  return (
    <section id="features" className="py-24 bg-[#080808]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-block text-xs text-[#3d69e8] font-medium tracking-widest uppercase mb-4">功能模块</div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">找到你的科研路径</h2>
          <div className="w-12 h-0.5 bg-[#3d69e8] mx-auto mb-4"></div>
          <p className="text-sm text-white/40">不同用户角色对应不同功能核心路径，点击展开查看完整流程</p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {tabs.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(i)}
              className={`flex flex-col items-center gap-1.5 px-6 py-4 rounded-xl border transition-all cursor-pointer ${
                activeTab === i
                  ? "bg-[#3d69e8]/10 border-[#3d69e8]/40 text-white"
                  : "bg-[#161616] border-white/8 text-white/50 hover:text-white/80 hover:border-white/15"
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${t.icon} text-xl ${activeTab === i ? "text-[#3d69e8]" : ""}`}></i>
              </div>
              <span className="text-sm font-medium whitespace-nowrap">{t.name}</span>
              <span className="text-xs whitespace-nowrap opacity-60">{t.role}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="flex flex-col gap-4">
            <div className="bg-[#111111] border border-white/8 rounded-2xl p-5">
              <div className="text-[10px] text-white/30 uppercase tracking-widest mb-2">核心矛盾</div>
              <p className="text-sm text-white/70 leading-relaxed">{tab.conflict}</p>
            </div>
            <div className="bg-[#111111] border border-white/8 rounded-2xl p-5">
              <div className="text-[10px] text-white/30 uppercase tracking-widest mb-2">核心价值</div>
              <p className="text-sm text-white/70 leading-relaxed">{tab.value}</p>
            </div>
            <div className="bg-[#111111] border border-white/8 rounded-2xl p-5">
              <div className="text-[10px] text-white/30 uppercase tracking-widest mb-2">情绪共鸣</div>
              <p className="text-sm text-white/70 leading-relaxed">{tab.emotion}</p>
            </div>
            <div className="flex gap-3">
              <a href="#" className="flex-1 text-center text-sm font-medium bg-[#3d69e8] hover:bg-[#3060d0] text-white px-3 py-2.5 rounded-xl transition-colors cursor-pointer no-underline whitespace-nowrap">
                ▸ {tab.cta}
              </a>
              <a href="#" className="flex-1 text-center text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-2.5 rounded-xl transition-colors cursor-pointer no-underline whitespace-nowrap">
                查看全部
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs text-white/30 uppercase tracking-widest mb-5">功能依赖核心路径</div>
            {tab.steps.map((step, i) => (
              <StepRow key={`${tab.key}-${i}`} step={step} isLast={i === tab.steps.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
